<script setup lang="ts">
import {
  TooltipPortal,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from "reka-ui"

defineProps<{ collapsed?: boolean }>()

const { $trackEvent } = useNuxtApp()
const { isStepCompleted, showKickoffPlan, steps } = useWorkspaceOnboarding()
const { data: workspace } = useWorkspaceSettings()

const visibleSteps = computed(() => steps.value.filter((step) => !step.hidden))
const totalSteps = computed(() => visibleSteps.value.length)
const completedCount = computed(
  () => visibleSteps.value.filter(isStepCompleted).length,
)

const nextIncompleteStep = computed(() =>
  getNextIncompleteOnboardingStep(workspace.value),
)
const markerDestination = computed(() => {
  if (showKickoffPlan.value) {
    return { name: "workspace-onboarding-kick-off" } as const
  }

  return nextIncompleteStep.value ?? ({ name: "dashboard" } as const)
})

const simulationKickoffDate = computed(
  () => workspace.value?.onboarding_state.simulation_date,
)
const trainingKickoffDate = computed(
  () => workspace.value?.onboarding_state.training_kickoff_date,
)

const progress = computed(() => {
  if (!totalSteps.value) return 0
  return Math.round((completedCount.value / totalSteps.value) * 100)
})

const progressStyle = computed(() => {
  if (progress.value == 0) {
    return { background: "var(--color-zinc-200)" }
  }

  return {
    background: `conic-gradient(
    var(--color-violet-400) 0%,
    var(--color-purple-400) ${progress.value / 2}%,
    var(--color-fuchsia-400) ${progress.value}%,
    var(--color-zinc-200) ${progress.value}% 100%
  )`,
  }
})

function handleClick() {
  $trackEvent("workspace_onboarding_progress_badge_clicked", {
    progress: `${progress.value}%`,
  })
}
</script>

<template>
  <TooltipProvider :delay-duration="100">
    <TooltipRoot>
      <TooltipTrigger as-child>
        <NuxtLink :to="markerDestination" @click="handleClick">
          <div
            class="group flex items-center rounded-full bg-white shadow-sm ring-1 ring-zinc-200/70 transition hover:bg-zinc-50"
            :class="
              collapsed
                ? 'justify-center p-1'
                : 'justify-between py-2 pr-3 pl-4'
            "
          >
            <div v-if="!collapsed" class="w-min">
              <TypographyText class="text-nowrap text-zinc-900">
                <TypographyStrong>
                  <template v-if="showKickoffPlan">
                    {{
                      $t("workspace_onboarding.progress_badge.kick_off_planned")
                    }}
                  </template>
                  <template v-else>
                    {{
                      $t("workspace_onboarding.progress_badge.workspace_setup")
                    }}
                    {{ progress }}%
                  </template>
                </TypographyStrong>
              </TypographyText>
            </div>

            <div class="shrink-0" :class="{ 'ml-1': !collapsed }">
              <div
                v-if="showKickoffPlan"
                class="flex size-6 items-center justify-center rounded-full bg-radial-[at_25%_25%] from-violet-200 to-fuchsia-50 to-75% text-violet-500"
              >
                <SvgoPikaRocketShip class="size-3.5" aria-hidden="true" />
              </div>
              <div
                v-else
                :style="progressStyle"
                class="relative size-6 rounded-full"
              >
                <div
                  class="absolute inset-1 rounded-full bg-white transition group-hover:bg-zinc-50"
                />
              </div>
            </div>
          </div>
        </NuxtLink>
      </TooltipTrigger>

      <TooltipPortal
        v-if="showKickoffPlan && simulationKickoffDate && trainingKickoffDate"
      >
        <WorkspaceOnboardingKickoffTooltip
          :simulation-kickoff-date
          :training-kickoff-date
        />
      </TooltipPortal>
    </TooltipRoot>
  </TooltipProvider>
</template>
